/**
 * ============================================================================
 * DeviationDetail — Phase 7 frontend (Deviation Management Module).
 * Owner: Phase 7 frontend agent.
 * Route: /deviations/:id (orchestrator wires this in router.tsx).
 * Purpose: Investigator + QA view of one deviation. Layout:
 *   - Header: number + title + classification chip + status chip + SLA badge.
 *   - Meta row: reporter, investigator, source area, occurred / reported / SLA.
 *   - Two-column body:
 *       Left  — Description block + Investigation notes (5-Why panel inline)
 *               + RCA summary block.
 *       Right — Dual-sign panel + Affected scope (equipment / docs / batches)
 *               + RCA history timeline.
 *
 * URS rows: DEV-002 (detail), DEV-005 (dual-sign UI), DEV-006 (5-Why),
 *           DEV-007 (Fishbone), DEV-008 (RCA traceability), DEV-013
 *           (equipment scope).
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DeviationDetail.module.css';
import {
  useDeviationDetailQuery,
  type NoteEntry,
  type NoteKind,
} from './useDeviationDetailQuery';
import { DualSignPanel } from './DualSignPanel';
import { DeviationActions } from './DeviationActions';
import { RcaTimeline } from './RcaTimeline';
import { FiveWhyPanel } from './FiveWhyPanel';
import { CostAttributionPanel } from '../Finance/CostAttribution';
import { RelatedRecords } from '../../components/RelatedRecords';
import { ImpactExtensionsPanel } from './ImpactExtensionsPanel';
import type {
  DeviationClassification,
  DeviationStatus,
} from '../DeviationTriage/useDeviationsQuery';

const CRIT_CLASS: Record<DeviationClassification, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
};

const STATUS_CLASS: Record<DeviationStatus, string> = {
  REPORTED: styles.statReported,
  TRIAGED: styles.statTriaged,
  INVESTIGATION: styles.statInvestigation,
  RCA: styles.statRca,
  CLOSED: styles.statClosed,
  CANCELLED: styles.statCancelled,
};

const RCA_KINDS: NoteKind[] = ['5_WHY', 'FISHBONE', 'RCA_HYPOTHESIS', 'RCA_CONFIRMED'];

export interface DeviationDetailProps {
  deviationId?: string;
}

function resolveDeviationId(propsId?: string): string | undefined {
  if (propsId) return propsId;
  if (typeof window === 'undefined') return undefined;
  const hash = window.location.hash;
  const m = hash.match(/#\/deviations\/([^/?#]+)/);
  return m && m[1] ? decodeURIComponent(m[1]) : undefined;
}

export function DeviationDetail({ deviationId: propId }: DeviationDetailProps = {}) {
  const { t, i18n } = useTranslation();
  const deviationId = resolveDeviationId(propId);
  const q = useDeviationDetailQuery(deviationId);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  if (!deviationId) {
    return (
      <div className={styles.screen}>
        <div className={styles.errorBanner} role="alert">
          {t('deviation.detail.missingId', {
            defaultValue: 'No deviation id provided.',
          })}
        </div>
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div className={styles.screen}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.skeletonRow} aria-hidden="true" />
        ))}
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className={styles.screen}>
        <div className={styles.errorBanner} role="alert">
          {t('deviation.detail.errors.loadFailed', {
            defaultValue: 'Could not load the deviation.',
          })}
        </div>
      </div>
    );
  }

  const d = q.data;
  const rcaNotes: NoteEntry[] = d.notes.filter((n) => RCA_KINDS.includes(n.kind));
  const otherNotes: NoteEntry[] = d.notes.filter((n) => !RCA_KINDS.includes(n.kind));

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>
            {d.deviationNumber} — {d.title}
          </h1>
          <p className={styles.subtitle}>
            {t('deviation.detail.subtitle', {
              defaultValue:
                'Deviation Management — investigation, RCA, classification, and closure.',
            })}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
            <span
              className={`${styles.chip} ${CRIT_CLASS[d.classification]}`}
              aria-label={t('deviation.classification.aria', {
                defaultValue: 'Classification: {{level}}',
                level: t(`deviation.classification.${d.classification}`, {
                  defaultValue: d.classification,
                }),
              })}
            >
              {t(`deviation.classification.${d.classification}`, {
                defaultValue: d.classification,
              })}
            </span>
            <span className={`${styles.chip} ${STATUS_CLASS[d.status]}`}>
              {t(`deviation.status.${d.status}`, { defaultValue: d.status })}
            </span>
            {d.slaBreached && (
              <span className={styles.slaBreachBadge}>
                {t('deviation.sla.breached', { defaultValue: 'SLA BREACHED' })}
              </span>
            )}
            {d.requiresCapa && (
              <span className={`${styles.chip} ${styles.statTriaged}`}>
                {t('deviation.requiresCapa', { defaultValue: 'CAPA required' })}
              </span>
            )}
          </div>
        </div>
      </header>

      <section
        className={styles.metaRow}
        aria-label={t('deviation.detail.meta.aria', { defaultValue: 'Metadata' })}
      >
        <div className={styles.metaTile}>
          <span className={styles.metaLabel}>
            {t('deviation.meta.sourceArea', { defaultValue: 'Source area' })}
          </span>
          <span className={styles.metaValue}>
            {t(`deviation.sourceArea.${d.sourceArea}`, { defaultValue: d.sourceArea })}
          </span>
        </div>
        <div className={styles.metaTile}>
          <span className={styles.metaLabel}>
            {t('deviation.meta.reporter', { defaultValue: 'Reporter' })}
          </span>
          <span className={styles.metaValue}>{d.reporterUserId}</span>
        </div>
        <div className={styles.metaTile}>
          <span className={styles.metaLabel}>
            {t('deviation.meta.investigator', { defaultValue: 'Investigator' })}
          </span>
          <span className={styles.metaValue}>
            {d.investigatorUserId ?? t('common.unassigned', { defaultValue: 'Unassigned' })}
          </span>
        </div>
        <div className={styles.metaTile}>
          <span className={styles.metaLabel}>
            {t('deviation.meta.occurred', { defaultValue: 'Occurred' })}
          </span>
          <span className={styles.metaValue}>{dateFmt.format(new Date(d.occurredAtUtc))}</span>
        </div>
        <div className={styles.metaTile}>
          <span className={styles.metaLabel}>
            {t('deviation.meta.reported', { defaultValue: 'Reported' })}
          </span>
          <span className={styles.metaValue}>{dateFmt.format(new Date(d.reportedAtUtc))}</span>
        </div>
        <div className={styles.metaTile}>
          <span className={styles.metaLabel}>
            {t('deviation.meta.slaDue', { defaultValue: 'SLA due' })}
          </span>
          <span className={styles.metaValue}>
            {d.slaDueUtc ? dateFmt.format(new Date(d.slaDueUtc)) : '—'}
          </span>
        </div>
      </section>

      <div className={styles.grid}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              {t('deviation.detail.description', { defaultValue: 'Description' })}
            </h2>
            <div className={styles.descriptionBlock}>{d.description}</div>
          </section>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              {t('deviation.detail.rcaSummary', { defaultValue: 'RCA summary' })}
            </h2>
            {d.rcaSummary ? (
              <>
                <div className={styles.descriptionBlock}>{d.rcaSummary}</div>
                <p className={styles.subtitle} style={{ margin: 0 }}>
                  {t('deviation.detail.rcaCompletedAt', {
                    defaultValue: 'Completed {{at}}',
                    at: d.rcaCompletedAtUtc
                      ? dateFmt.format(new Date(d.rcaCompletedAtUtc))
                      : t('common.tbd', { defaultValue: 'TBD' }),
                  })}
                </p>
              </>
            ) : (
              <p className={styles.emptyNote}>
                {t('deviation.detail.rcaNotYet', {
                  defaultValue:
                    'RCA summary not yet captured. Major/Critical deviations require ≥ 50 characters before closure (DEV-008).',
                })}
              </p>
            )}
          </section>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              {t('deviation.detail.rcaNotes', { defaultValue: '5-Why / RCA notes' })}
            </h2>
            {rcaNotes.length === 0 ? (
              <p className={styles.emptyNote}>
                {t('deviation.detail.rcaNotesEmpty', {
                  defaultValue: 'No RCA notes yet.',
                })}
              </p>
            ) : (
              <ul className={styles.notesList}>
                {rcaNotes.map((n) => (
                  <li key={n.noteId} className={styles.noteItem}>
                    <div className={styles.noteHeader}>
                      <span
                        className={`${styles.noteKind} ${n.kind === 'RCA_CONFIRMED' ? styles.noteKindRcaConfirmed : styles.noteKindRca}`}
                      >
                        {t(`deviation.noteKind.${n.kind}`, { defaultValue: n.kind })}
                      </span>
                      <span className={styles.noteMeta}>
                        {dateFmt.format(new Date(n.authoredAtUtc))}
                      </span>
                    </div>
                    {n.kind === '5_WHY' ? (
                      <FiveWhyPanel body={n.body} />
                    ) : (
                      <div className={styles.noteBody}>{n.body}</div>
                    )}
                    <span className={styles.noteMeta}>
                      {t('deviation.noteMeta.authoredBy', {
                        defaultValue: 'By {{u}}',
                        u: n.authoredByUserId,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              {t('deviation.detail.notes', {
                defaultValue: 'Investigation notes',
              })}
            </h2>
            {otherNotes.length === 0 ? (
              <p className={styles.emptyNote}>
                {t('deviation.detail.notesEmpty', {
                  defaultValue: 'No investigation notes yet.',
                })}
              </p>
            ) : (
              <ul className={styles.notesList}>
                {otherNotes.map((n) => (
                  <li key={n.noteId} className={styles.noteItem}>
                    <div className={styles.noteHeader}>
                      <span className={styles.noteKind}>
                        {t(`deviation.noteKind.${n.kind}`, { defaultValue: n.kind })}
                      </span>
                      <span className={styles.noteMeta}>
                        {dateFmt.format(new Date(n.authoredAtUtc))}
                      </span>
                    </div>
                    <div className={styles.noteBody}>{n.body}</div>
                    {n.evidenceDocId && (
                      <span className={styles.noteMeta}>
                        {t('deviation.noteMeta.evidence', {
                          defaultValue: 'Evidence: {{d}}',
                          d: n.evidenceDocId,
                        })}
                      </span>
                    )}
                    <span className={styles.noteMeta}>
                      {t('deviation.noteMeta.authoredBy', {
                        defaultValue: 'By {{u}}',
                        u: n.authoredByUserId,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <DeviationActions d={d} onChanged={() => void q.refetch()} />

          <DualSignPanel
            classification={d.classification}
            dualSign={d.dualSign}
            closedAtUtc={d.closedAtUtc}
          />

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              {t('deviation.detail.scope', { defaultValue: 'Affected scope' })}
            </h2>
            <div className={styles.metaTile}>
              <span className={styles.metaLabel}>
                {t('deviation.detail.scope.equipment', { defaultValue: 'Equipment' })}
              </span>
              <span className={styles.metaValue}>
                {d.affectedEquipmentIds.length > 0
                  ? d.affectedEquipmentIds.join(', ')
                  : t('common.none', { defaultValue: 'None' })}
              </span>
            </div>
            <div className={styles.metaTile}>
              <span className={styles.metaLabel}>
                {t('deviation.detail.scope.docs', { defaultValue: 'Documents' })}
              </span>
              <span className={styles.metaValue}>
                {d.affectedDocIds.length > 0
                  ? d.affectedDocIds.join(', ')
                  : t('common.none', { defaultValue: 'None' })}
              </span>
            </div>
            <div className={styles.metaTile}>
              <span className={styles.metaLabel}>
                {t('deviation.detail.scope.batches', { defaultValue: 'Batches' })}
              </span>
              <span className={styles.metaValue}>
                {d.affectedBatchRefs.length > 0
                  ? d.affectedBatchRefs.join(', ')
                  : t('common.none', { defaultValue: 'None' })}
              </span>
            </div>
            {d.capaId && (
              <div className={styles.metaTile}>
                <span className={styles.metaLabel}>
                  {t('deviation.detail.scope.capaId', { defaultValue: 'Linked CAPA' })}
                </span>
                <span className={styles.metaValue}>{d.capaId}</span>
              </div>
            )}
          </section>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>
              {t('deviation.detail.timeline', { defaultValue: 'History timeline' })}
            </h2>
            <RcaTimeline history={d.history} />
          </section>

          {/* Finance module — Cost Attribution (Tier 1.1 v0.13).
              Gated by user role (FinanceReader+); hidden for non-finance users. */}
          {/* C-9 — GMP impact assessment + containment + investigation extensions. */}
          {d.impact && (
            <ImpactExtensionsPanel
              deviationId={d.deviationId}
              immediateActions={d.immediateActions ?? null}
              impact={d.impact}
              extensions={d.extensions ?? []}
            />
          )}

          {/* C-4 — cross-module Related Records (event-linking engine). */}
          <RelatedRecords entityType="DEVIATION" entityId={d.deviationId} />

          <CostAttributionPanel
            subjectEntityType="DEVIATION"
            subjectEntityId={d.deviationId}
            subjectDisplayLabel={`${d.deviationNumber} — ${d.title}`}
          />
        </div>
      </div>
    </div>
  );
}
